import { MouseEvent, type ReactElement, useRef } from 'react';
import type { Table } from '@tanstack/react-table';

import { Checkbox } from '@/ux-common/components/checkbox/checkbox';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@/ux-common/components/tooltip/tooltip';

interface SelectHeaderProps<TData> {
  /** Tooltip shown when the checkbox is disabled because no row can be selected. */
  disabledTooltip?: string;
  /**
   * Controls the header checkbox behavior for bulk selection.
   *
   * - `'all'` — Full select-all checkbox (default)
   * - `'deselect-only'` — Disabled when empty, indeterminate when selected, always deselects
   * - `'none'` — No header checkbox
   */
  selectAllMode?: 'all' | 'deselect-only' | 'none';
  table: Table<TData>;
}

/**
 * Header checkbox for bulk selection.
 *
 * Displays three states:
 * - Checked: All rows are selected
 * - Unchecked: No rows are selected
 * - Indeterminate: Some rows are selected
 *
 * In 'all' mode the checkbox is disabled when no row can be selected and none is
 * selected, with an optional tooltip explaining why. If rows are still selected
 * but none can be selected, it stays enabled and clicking it clears the selection.
 *
 * When selectAllMode is 'deselect-only', operates in deselect-only mode:
 * disabled when nothing is selected, indeterminate when any rows are selected,
 * and clicking always deselects all.
 */
export const SelectHeader = <TData,>({
  disabledTooltip,
  selectAllMode = 'all',
  table,
}: SelectHeaderProps<TData>): ReactElement | null => {
  const ref = useRef<HTMLButtonElement>(null);

  if (selectAllMode === 'none') {
    return null;
  }

  const isAllSelected = table.getIsAllRowsSelected();
  const isSomeSelected = table.getIsSomeRowsSelected();
  const isDeselectOnly = selectAllMode === 'deselect-only';
  const hasAnySelected = isAllSelected || isSomeSelected;
  const canSelectAny = table.getCoreRowModel().rows.some((row) => row.getCanSelect());

  // Determine checkbox state
  let checkedState: boolean | 'indeterminate';
  if (isDeselectOnly) {
    // Deselect-only: never show fully checked, show indeterminate when any selected
    checkedState = hasAnySelected ? 'indeterminate' : false;
  } else {
    checkedState = isAllSelected ? true : isSomeSelected ? 'indeterminate' : false;
  }

  // In 'all' mode the header only goes inert when there is nothing to select and
  // nothing to clear; rows selected before they became unselectable stay clearable.
  const isDisabled = isDeselectOnly ? !hasAnySelected : !canSelectAny && !hasAnySelected;
  // With nothing selectable, the only thing a click can do is clear the selection.
  const clearsOnly = isDeselectOnly || !canSelectAny;
  const tooltipText = !isDeselectOnly && isDisabled ? disabledTooltip : undefined;

  const handleCellClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isDisabled) return;
    ref.current?.click();
  };

  const checkbox = (
    <div
      className="flex items-center justify-center w-full h-full"
      onMouseDown={(e) => {
        // Prevent table drag-scroll from treating checkbox clicks as drag starts.
        e.stopPropagation();
      }}
      onPointerDown={(e) => {
        // Keep behavior consistent for pointer events (touch/pen/mouse).
        e.stopPropagation();
      }}
      onClick={handleCellClick}
      role="button"
    >
      <Checkbox
        ref={ref}
        checked={checkedState}
        disabled={isDisabled}
        onCheckedChange={(checked) => {
          if (clearsOnly) {
            // Deselect-only mode, or nothing left to select: always deselect all
            table.toggleAllRowsSelected(false);
          } else {
            // Normal mode: toggle based on checked state
            table.toggleAllRowsSelected(checked === true);
          }
        }}
        aria-label={isDeselectOnly ? 'Deselect all rows' : 'Select all rows'}
      />
    </div>
  );

  if (tooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{checkbox}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="right">
            {tooltipText}
            <TooltipArrow />
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  }

  return checkbox;
};

SelectHeader.displayName = 'SelectHeader';
